import React, { useState } from 'react';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { FloralInput, FloralButton } from './ui/FormComponents';
import { OptimizedFloralCard } from './ui/OptimizedComponents';

interface AdminLoginProps {
  onLoginSuccess: () => void;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onLoginSuccess }) => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);

  // Get credentials from environment variables for security
  const ADMIN_CREDENTIALS = {
    username: import.meta.env.VITE_ADMIN_USERNAME || 'admin',
    password: import.meta.env.VITE_ADMIN_PASSWORD || 'defaultpassword'
  };

  // Validate that environment variables are set
  if (!import.meta.env.VITE_ADMIN_USERNAME || !import.meta.env.VITE_ADMIN_PASSWORD) {
    console.warn('Admin credentials not properly configured in environment variables');
  }

  const MAX_ATTEMPTS = 3;
  const LOCKOUT_TIME = 15 * 60 * 1000; // 15 minutes

  const handleInputChange = (field: string, value: string) => {
    setCredentials(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const checkRateLimit = (): boolean => {
    const lastAttempt = localStorage.getItem('adminLastAttempt');
    const attemptCount = parseInt(localStorage.getItem('adminAttempts') || '0');
    
    if (lastAttempt && attemptCount >= MAX_ATTEMPTS) {
      const timeSinceLastAttempt = Date.now() - parseInt(lastAttempt);
      if (timeSinceLastAttempt < LOCKOUT_TIME) {
        const remainingTime = Math.ceil((LOCKOUT_TIME - timeSinceLastAttempt) / 60000);
        setError(`Too many failed attempts. Please try again in ${remainingTime} minutes.`);
        return false;
      } else {
        // Reset attempts after lockout period
        localStorage.removeItem('adminAttempts');
        localStorage.removeItem('adminLastAttempt');
      }
    }
    return true;
  };

  const recordFailedAttempt = () => {
    const currentAttempts = parseInt(localStorage.getItem('adminAttempts') || '0') + 1;
    localStorage.setItem('adminAttempts', currentAttempts.toString());
    localStorage.setItem('adminLastAttempt', Date.now().toString());
    setAttempts(currentAttempts);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!checkRateLimit()) {
      return;
    }

    setIsLoading(true);
    setError('');

    // Simulate network delay for security
    await new Promise(resolve => setTimeout(resolve, 1000));

    if (credentials.username === ADMIN_CREDENTIALS.username && 
        credentials.password === ADMIN_CREDENTIALS.password) {
      // Clear failed attempts on successful login
      localStorage.removeItem('adminAttempts');
      localStorage.removeItem('adminLastAttempt');
      
      // Set authentication session
      const sessionData = {
        authenticated: true,
        timestamp: Date.now(),
        expires: Date.now() + (2 * 60 * 60 * 1000) // 2 hours
      };
      sessionStorage.setItem('adminSession', JSON.stringify(sessionData));
      
      onLoginSuccess();
    } else {
      recordFailedAttempt();
      setError('Invalid username or password');
    }

    setIsLoading(false);
  };

  const remainingAttempts = Math.max(0, MAX_ATTEMPTS - attempts);

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-dusty-rose-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <OptimizedFloralCard className="bg-white/95 backdrop-blur-sm shadow-2xl" showCornerDecorations={false}>
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Admin Login</h1>
            <p className="text-gray-600">Access the wedding RSVP dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Lock className="inline w-4 h-4 mr-2 text-sage-500" />
                Username
              </label>
              <FloralInput
                value={credentials.username}
                onChange={(value) => handleInputChange('username', value)}
                placeholder="Enter username"
                required
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Lock className="inline w-4 h-4 mr-2 text-sage-500" />
                Password
              </label>
              <div className="relative">
                <style dangerouslySetInnerHTML={{
                  __html: `
                    input[type="password"]::-ms-reveal,
                    input[type="password"]::-ms-clear {
                      display: none;
                    }
                    input[type="password"]::-webkit-credentials-auto-fill-button,
                    input[type="password"]::-webkit-strong-password-auto-fill-button {
                      display: none !important;
                    }
                  `
                }} />
                <FloralInput
                  type={showPassword ? 'text' : 'password'}
                  value={credentials.password}
                  onChange={(value) => handleInputChange('password', value)}
                  placeholder="Enter password"
                  required
                  disabled={isLoading}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-700 text-sm">{error}</p>
                {attempts > 0 && remainingAttempts > 0 && (
                  <p className="text-red-600 text-xs mt-1">
                    {remainingAttempts} attempt{remainingAttempts !== 1 ? 's' : ''} remaining
                  </p>
                )}
              </div>
            )}

            <FloralButton
              type="submit"
              disabled={isLoading || !credentials.username || !credentials.password}
              className="w-full"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Signing in...
                </div>
              ) : (
                'Sign In'
              )}
            </FloralButton>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Secure access to wedding RSVP management
            </p>
          </div>
        </OptimizedFloralCard>
      </div>
    </div>
  );
};

export default AdminLogin;